import React, { useMemo, useState, useEffect } from 'react';
import MatchCard from '../../MatchCard/MatchCard';
import SectionTitle from '../../SectionTitle/SectionTitle';
import smallShield from '../../../assets/smallShield.png';
import './styles.scss';
import { useTranslation } from 'hooks/useTranslation';
import { TRANSLATION_KEYS } from '../GroupStage/constants';
import { formatMatchTimeByRegion } from '../../../../utils/formatMatchTime';
import { getRegion } from 'utils/Region';
import { useMundialMetrics } from '../../../../hooks/useMundialMetrics';
import { INTERACTION_TYPES } from '../../../../hooks/useMundialMetrics/constants';
import { ACTIONABLE_MATCH_STATUSES } from '../../../../hooks/useMundialMetrics/utils/buildMatchValues';
import { useMundialScrollTracker } from '../../../../hooks/useMundialScrollTracker';
import { getVisibleMatchNames } from '../../../utils/getVisibleMatchNames';

const SEMI_MOBILE_MEDIA = '(max-width: 1024px)';

function formatMatchTime(m, isMobile) {
  return formatMatchTimeByRegion(m, {
    includeDate: true,
    bracketSingleLine: !isMobile,
  });
}

function renderMatch(m, isMobile, extraProps = {}, onMatchClick) {
  if (!m) {
    return (
      <MatchCard
        time="Por definir"
        teamA="Por definir"
        teamB="Por definir"
        stadium=""
        abbreviateTeamNamesOnMobile
        noFlagPlaceholderSrc={smallShield}
        {...extraProps}
      />
    );
  }
  return (
    <MatchCard
      key={m.matchId}
      time={formatMatchTime(m, isMobile)}
      teamA={m.homeTeam?.name || 'Por definir'}
      teamB={m.awayTeam?.name || 'Por definir'}
      shortNameA={m.homeTeam?.shortName}
      shortNameB={m.awayTeam?.shortName}
      flagUrlA={m.homeTeam?.flagUrl}
      flagUrlB={m.awayTeam?.flagUrl}
      stadium={m.venue ? `${m.venue.name}, ${m.venue.city}` : ''}
      status={m.status}
      score={m.score}
      matchMinute={m.matchProgress?.minute}
      isBroadcastAvailable={m.broadcast?.isAvailable}
      abbreviateTeamNamesOnMobile
      noFlagPlaceholderSrc={smallShield}
      {...extraProps}
      onClick={onMatchClick ? () => onMatchClick(m) : undefined}
    />
  );
}

export default function SemiStage({ stageData, nextStageData, thirdPlaceData, onMatchCardClick }) {
  useMundialScrollTracker({
    pageTitleSegments: ['semifinal y 3°'],
    contentSubsection: 'semifinal y 3°',
    moduloName: '',
    getVisibleContentIds: getVisibleMatchNames,
  });

  const { trackSelectContent } = useMundialMetrics();

  const handleMatchClick = (match) => {
    onMatchCardClick?.(match.matchId);
    if (!ACTIONABLE_MATCH_STATUSES.includes(match.status)) return;
    const matchName = `${match.homeTeam?.name || ''} vs ${match.awayTeam?.name || ''}`;
    trackSelectContent({
      pageTitleSegments: ['semifinal y 3°'],
      pageTitle: matchName,
      contentSubsection: 'semifinal y 3°',
      moduloName: '',
      interactionType: INTERACTION_TYPES.CARD_SELECT,
      match,
    });
  };

  const { translationMapper } = useTranslation();
  const translatedTexts = useMemo(
    () => translationMapper(TRANSLATION_KEYS),
    [],
  );

  const [isSemiMobileLayout, setIsSemiMobileLayout] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(SEMI_MOBILE_MEDIA).matches : false,
  );

  useEffect(() => {
    const mq = window.matchMedia(SEMI_MOBILE_MEDIA);
    const sync = () => setIsSemiMobileLayout(mq.matches);
    sync();
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', sync);
      return () => mq.removeEventListener('change', sync);
    }
    mq.addListener(sync);
    return () => mq.removeListener(sync);
  }, []);

  const semiMatches = stageData?.data?.rounds?.[0]?.matches || [];
  const stageName = translatedTexts['TITLE_1_SEMI_FINALS'];
  const finalMatch = nextStageData?.data?.rounds?.[0]?.matches?.[0] || null;
  const finalName = translatedTexts['TITLE_2_SEMI_FINALS'];
  const thirdMatch = thirdPlaceData?.data?.rounds?.[0]?.matches?.[0] || null;
  const thirdName = translatedTexts['TITLE_3_SEMI_FINALS'];

  return (
    <div className="semi-stage">
      <div className="semi-content">
        <div className="semi-column semi-column--left">
          <div className="semi-section-title">
            <SectionTitle>{stageName}</SectionTitle>
          </div>
          <div className="semi-matches">
            {semiMatches.length > 0 ? (
              semiMatches.map(m =>
                renderMatch(m, isSemiMobileLayout, { lineLeft: true, lineLeftWidth: 137 }, handleMatchClick),
              )
            ) : (
              <>
                {renderMatch(null, isSemiMobileLayout, { lineLeft: true, lineLeftWidth: 137 }, handleMatchClick)}
                {renderMatch(null, isSemiMobileLayout, { lineLeft: true, lineLeftWidth: 137 }, handleMatchClick)}
              </>
            )}
          </div>
        </div>

        <div className="semistage-connectors" aria-hidden="true">
          <div className="semistage-bracket semistage-bracket--left" />
        </div>

        <div className="semi-column semi-column--right">
          <div className="semi-right-block semi-right-block--final">
            <div className="semi-section-title">
              <SectionTitle>{finalName}</SectionTitle>
            </div>
            <div className="semi-stage__final-match">
              {renderMatch(
                finalMatch,
                isSemiMobileLayout,
                {
                  lineLeft: true,
                  lineLeftWidth: 164,
                  lineRight: true,
                  lineRightWidth: 135,
                  wcSemiBracketLines: 'final',
                },
                handleMatchClick,
              )}
            </div>
          </div>
          <div className="semi-right-block semi-right-block--third">
            <div className="semi-section-title">
              <SectionTitle>{thirdName}</SectionTitle>
            </div>
            <div className="semi-stage__third-match">
              {renderMatch(
                thirdMatch,
                isSemiMobileLayout,
                { lineLeft: true, lineLeftWidth: 165, wcSemiBracketLines: 'third' },
                handleMatchClick,
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
